import { useEffect, useId, useState } from 'react';
import styled from 'styled-components';

import { PROPERTY_CAPTION_MARGIN } from '../sidepanel/PropertyCaption';
import { QuietValueInput, RangeSlider } from './sliderPrimitives';

import type { KeyboardEvent } from 'react';

type Props = {
  /** Title Case, the field's name: "Horizontal Alignment". */
  label: string;
  value: number;
  min: number;
  max: number;
  unit?: string;
  /**
   * A value that sits at the track's middle when the range isn't symmetric
   * around it (Zoom: 20–500% with 100% centred). Piecewise linear either side.
   */
  midpoint?: number;
  /** Values the thumb snaps to when dragged within ~3% of them (50%, 100%). */
  snapTo?: number[];
  /** Called as the thumb moves and when a typed value is committed. */
  onChange: (value: number) => void;
  disabled?: boolean;
};

const SNAP_THRESHOLD = 3;

/**
 * A side panel's slider: the label row with the value demoted to its right as
 * a quiet, editable field, and the gold hairline under it. No box around it, no
 * icon, no second boxed number (docs/design-system.md § "Panel Fields").
 *
 * Replaces the Photo / Video panels' `SliderAndIcon`: a bordered box holding an
 * icon, the slider and another bordered value box (PLAT-12137).
 */
const PanelSliderField = ({
  label,
  value,
  min,
  max,
  unit = '%',
  midpoint,
  snapTo = [],
  onChange,
  disabled = false,
}: Props) => {
  const inputId = useId();
  const [current, setCurrent] = useState(value);
  const [draft, setDraft] = useState<string | null>(null);

  useEffect(() => {
    setCurrent(value);
  }, [value]);

  // Track position (0–100) ↔ value, piecewise around the midpoint if any.
  const toPosition = (v: number) => {
    if (midpoint === undefined) return ((v - min) / (max - min)) * 100;
    return v <= midpoint
      ? ((v - min) / (midpoint - min)) * 50
      : 50 + ((v - midpoint) / (max - midpoint)) * 50;
  };
  const toValue = (p: number) => {
    if (midpoint === undefined) return min + (p / 100) * (max - min);
    return p <= 50
      ? min + (p / 50) * (midpoint - min)
      : midpoint + ((p - 50) / 50) * (max - midpoint);
  };

  const clamp = (v: number) => Math.min(max, Math.max(min, v));
  const position = toPosition(clamp(current));

  const set = (v: number) => {
    const next = Math.round(clamp(v));
    // A drag reports every track step; only a new value is worth an update.
    if (next === current) return;
    setCurrent(next);
    onChange(next);
  };

  const handleRange = (p: number) => {
    const snap = snapTo.find(
      s => Math.abs(toPosition(s) - p) <= SNAP_THRESHOLD,
    );
    set(snap ?? toValue(p));
  };

  const commitDraft = () => {
    if (draft === null) return;
    const typed = parseFloat(draft);
    setDraft(null);
    if (!Number.isNaN(typed)) set(typed);
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') e.currentTarget.blur();
    if (e.key === 'Escape') {
      setDraft(null);
      e.currentTarget.blur();
    }
  };

  const shown = `${Math.round(current)}${unit}`;

  return (
    <Field>
      <Head>
        <Label htmlFor={inputId}>{label}</Label>
        <QuietValueInput
          id={inputId}
          type="text"
          inputMode="decimal"
          $invalid={false}
          disabled={disabled}
          value={draft ?? shown}
          onFocus={e => {
            setDraft(String(Math.round(current)));
            e.currentTarget.select();
          }}
          onChange={e => setDraft(e.target.value)}
          onBlur={commitDraft}
          onKeyDown={handleKeyDown}
        />
      </Head>
      <Track>
        <RangeSlider
          type="range"
          aria-label={`${label} slider`}
          aria-valuetext={shown}
          min={0}
          max={100}
          step={1}
          disabled={disabled}
          value={position}
          $fill={position}
          onChange={e => handleRange(Number(e.target.value))}
        />
      </Track>
    </Field>
  );
};

export default PanelSliderField;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
`;

const Head = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--spacing-sm);
  margin: ${PROPERTY_CAPTION_MARGIN};
  /* The value field is 24px; pull the row in so the label sits where every
     other caption does. */
  margin-bottom: 2px;
  min-height: 24px;
`;

const Label = styled.label`
  min-width: 0;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  color: var(--text-muted);
  font-weight: var(--font-weight-medium);
  font-size: var(--text-xs);
  line-height: var(--leading-text-xs);
`;

/* Tall enough for the 14px thumb, so the hairline is never clipped. */
const Track = styled.div`
  display: flex;
  align-items: center;
  height: 16px;
  padding: 0 1px;
`;
